import { intersectionBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackCanvasMediaImported } from '@air/analytics';
import { type Board, type Clip, type Tag } from '@air/api/types';
import {
  buildNodesFromPayloads,
  CANVAS_IMAGE_NODE_SPACING,
  clipToCanvasAssetPayload,
  commands,
  DEFAULT_CANVAS_IMAGE_WIDTH,
  getAssetDropToastMessage,
  type ImageNodesLayout,
  useCommandHistory,
  useEditCanvasStore,
} from '@air/feature-edit-canvas';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { resetSelectedItemsAction, selectedItemsSelector } from '@air/redux-selected-items';

import { useCanvasInsertionToastStore } from '~/components/Canvas/store/useCanvasInsertionToastStore';
import { EditClipsTagsModal } from '~/components/Modals/EditClipsTagsModal';
import { SharePrivateBoardModal } from '~/components/SharePrivateBoardModal/SharePrivateBoardModal';
import { SharePrivateCanvasModal } from '~/components/SharePrivateCanvasModal/SharePrivateCanvasModal';
import { useSharePrivateAsset } from '~/components/SharePrivateClipModal/hooks/useSharePrivateAsset';
import { CreateShowcaseModal } from '~/components/ShowcaseModal/CreateShowcaseModal';
import { useShowcaseCreatedToast } from '~/components/ShowcaseModal/hooks/useShowcaseCreatedToast';
import { applySelectAllCompactTuck } from '~/components/v2/components/private-selection-bar/hooks/applySelectionBarOverflowTuck';
import { usePrivateSelectionBarMenu } from '~/components/v2/components/private-selection-bar/hooks/usePrivateSelectionBarMenu';
import {
  DEFAULT_SELECTION_BAR_LAYOUT_TIER_FLAGS,
  type SelectionBarLayoutTierFlags,
} from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarLayoutTier';
import { type SelectionBarActions } from '~/components/v2/components/shared/selectionBar/selectionBarActions';
import { useSelectionBarDownloads } from '~/components/v2/hooks/useSelectionBarDownloads';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useBulkDownloadPrivateAssets } from '~/hooks/useBulkDownloadPrivateAssets';
import { useCanConsumeMoreCredits } from '~/hooks/useCanConsumeMoreCredits';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { useDownloadPrivateAsset } from '~/hooks/useDownloadPrivateAsset';
import { useDownloadPrivateBoard } from '~/hooks/useDownloadPrivateBoard';
import { useDownloadPrivateItems } from '~/hooks/useDownloadPrivateItems';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { usePrivateSelectAllBoardMembershipActions } from '~/hooks/usePrivateSelectAllBoardMembershipActions';
import { usePrivateSelectAllTotalCount } from '~/hooks/usePrivateSelectAllTotalCount';
import { useStartPrivateAssetComparison } from '~/hooks/useStartPrivateAssetComparison';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import {
  hasOnlyCanvasesSelectedSelector,
  selectedBoardIdsForShowcaseSelector,
  selectedBoardsSelector,
  selectedClipsAssetIdsSelector,
  selectedFileClipsSelector,
  selectedMediaClipsSelector,
} from '~/store/selectedItems/selectors';
import { useToggleFavoritedBoard } from '~/swr-hooks/boards/useToggleFavoritedBoard';
import { useToggleFavorite } from '~/swr-hooks/clips/useToggleFavorite';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import { canChangeAssetCustomFields } from '~/utils/permissions/assetPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';
import { canCreateCanvasInWorkspace } from '~/utils/permissions/workspacePermissions';
import { useAirStore } from '~/utils/ReduxUtils';

const GRID_GAP = 40;

export type UsePrivateSelectionBarActionsResult = {
  menuOptions: DropdownMenuOption[];
  actions: SelectionBarActions;
  /** The right panel already names the board, so repeating it in the count is noise. */
  omitBoardName: boolean;
  /** Beside an open right panel there isn't room for the count and select-all on one line. */
  stackLeftBarButtons: boolean;
  selectAllEnabled: boolean;
  selectAllTotalCount?: number | null;
};

/**
 * Everything the private selection bar can do with the current selection: permissions, handlers,
 * modals, analytics, and the resulting menu and action descriptors.
 *
 * Both private bars run this identically — they differ only in where they render and what their
 * buttons look like.
 */
export const usePrivateSelectionBarActions = (
  layoutTierFlags: SelectionBarLayoutTierFlags = DEFAULT_SELECTION_BAR_LAYOUT_TIER_FLAGS,
): UsePrivateSelectionBarActionsResult => {
  const isRightPanelOpen = useWorkspaceRightPanelOpen();

  const { collectionsEnabled } = useFeatureFlags();
  const { selectAllEnabled, generativeCanvasEnabled } = useTemporaryFeatureFlags();

  const dispatch = useDispatch();
  const store = useAirStore();
  const setInsertionToastMessage = useCanvasInsertionToastStore((state) => state.setMessage);
  const canvasReadOnly = useEditCanvasStore((state) => state.readOnly);
  const execute = useCommandHistory((state) => state.execute);
  const selectedMediaClipItems = useSelector(selectedMediaClipsSelector);
  const selectedFileItems = useSelector(selectedFileClipsSelector);
  const hasOnlyCanvasesSelected = useSelector(hasOnlyCanvasesSelectedSelector);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
  const selectAllTotalCount = usePrivateSelectAllTotalCount();
  const {
    canRunBoardMembershipAction: canRunSelectAllBoardMembershipAction,
    openAddToBoardModal: openSelectAllAddToBoardModal,
    openMoveItemsModal: openSelectAllMoveItemsModal,
  } = usePrivateSelectAllBoardMembershipActions();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { openDraftCanvas } = useDraftCanvas();
  const { canConsumeMoreCredits, showCreditsDepletedModal } = useCanConsumeMoreCredits();

  const canDownloadAllMediaClips = selectedMediaClipItems.every(({ id }) => canGenerateZip(getClipPermissions(id)));
  const canDownloadAllFiles = selectedFileItems.every(({ id }) => canGenerateZip(getClipPermissions(id)));
  const canDownloadAllClips = canDownloadAllMediaClips && canDownloadAllFiles;
  const allSelectedClips = useMemo(
    () => [...selectedMediaClipItems, ...selectedFileItems],
    [selectedMediaClipItems, selectedFileItems],
  );
  const canEditAllCustomFields = useMemo(
    () => allSelectedClips.every(({ id }) => canChangeAssetCustomFields(getClipPermissions(id))),
    [allSelectedClips, getClipPermissions],
  );
  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  const selectAllCustomFieldMenuOptions = useMemo(
    () => (canEditAllCustomFields ? getCustomFieldsMenuOptions({ assets: allSelectedClips }) : []),
    [allSelectedClips, canEditAllCustomFields, getCustomFieldsMenuOptions],
  );

  const selectedBoardItems = useSelector(selectedBoardsSelector);
  const canDownloadAllBoards = selectedBoardItems.every(({ id }) => canGenerateZip(getBoardPermissions(id)));

  const { toggleFavoritedBoard } = useToggleFavoritedBoard();
  const { toggleFavorite } = useToggleFavorite();

  const { createBulkDownloadHandler, createAssetDownloadHandler, createBoardDownloadHandler } =
    useSelectionBarDownloads();
  const { downloadPrivateItems } = useDownloadPrivateItems();
  const { downloadPrivateAsset } = useDownloadPrivateAsset();
  const { downloadPrivateBoard } = useDownloadPrivateBoard();
  const { bulkDownloadPrivateAssets } = useBulkDownloadPrivateAssets();

  const handleBulkDownload = useMemo(
    () =>
      createBulkDownloadHandler({
        downloadItems: downloadPrivateItems,
        bulkDownloadAssets: bulkDownloadPrivateAssets,
      }),
    [createBulkDownloadHandler, downloadPrivateItems, bulkDownloadPrivateAssets],
  );

  const handleAssetDownload = useMemo(
    () => createAssetDownloadHandler(downloadPrivateAsset),
    [createAssetDownloadHandler, downloadPrivateAsset],
  );

  const handleBoardDownload = useMemo(
    () => createBoardDownloadHandler(downloadPrivateBoard),
    [createBoardDownloadHandler, downloadPrivateBoard],
  );

  const [showEditClipsTagsModal] = useAirModal(EditClipsTagsModal);
  const [showEditTagsToClipsModal] = useAirModal(EditClipsTagsModal);
  const [sharePrivateBoard] = useAirModal(SharePrivateBoardModal);
  const [sharePrivateCanvas] = useAirModal(SharePrivateCanvasModal);
  const { openShareModal } = useSharePrivateAsset({ trackLocation: 'action-bar' });
  const { startPrivateAssetComparison } = useStartPrivateAssetComparison();
  const [showCreateShowcaseModal, closeCreateShowcaseModal] = useAirModal(CreateShowcaseModal);
  const { showShowcaseCreatedToast } = useShowcaseCreatedToast({ closeCreateShowcaseModal });

  const { trackCanvasMediaImported } = useTrackCanvasMediaImported();

  const handleBoardFavorite = useCallback(
    (board: Board) => {
      void toggleFavoritedBoard({ board, trackLocation: 'action-bar' });
    },
    [toggleFavoritedBoard],
  );

  const handleClipFavorite = useCallback(
    (clips: Clip[]) => {
      toggleFavorite({ clips, trackLocation: 'action-bar' });
    },
    [toggleFavorite],
  );

  const handleBoardShare = useCallback(
    (boardId: string) => {
      sharePrivateBoard({ boardId, trackLocation: 'action-bar' });
    },
    [sharePrivateBoard],
  );

  const handleCanvasShare = useCallback(
    (canvasId: string) => {
      sharePrivateCanvas({ canvasId, trackLocation: 'action-bar' });
    },
    [sharePrivateCanvas],
  );

  const handleClipShare = useCallback(
    (clip: Clip) => {
      openShareModal(clip);
    },
    [openShareModal],
  );

  const handleSingleClipEditTags = useCallback(
    (clip: Clip, tags: Tag[]) => {
      showEditClipsTagsModal({
        clipIds: [clip.id],
        trackLocation: 'action-bar',
        initialTags: tags,
      });
    },
    [showEditClipsTagsModal],
  );

  const handleCollectionShare = useCallback(() => {
    showCreateShowcaseModal({
      assetIds: selectedClipsAssetIdsSelector(store.getState()),
      boardIds: selectedBoardIdsForShowcaseSelector(store.getState()),
      onShowcaseCreated: showShowcaseCreatedToast,
    });
  }, [showCreateShowcaseModal, showShowcaseCreatedToast, store]);

  const handleCompare = useCallback(() => {
    const [primaryClip, comparisonClip] = selectedMediaClipsSelector(store.getState());

    if (!primaryClip || !comparisonClip) {
      return;
    }

    startPrivateAssetComparison({
      comparisonClipId: comparisonClip.id,
      isAssetModalAlreadyOpen: false,
      primaryClip,
    });
  }, [startPrivateAssetComparison, store]);

  const handleMultipleClipsEditTags = useCallback(() => {
    const commonTags = intersectionBy(...allSelectedClips.map(({ tags }) => tags || []), 'id');
    showEditTagsToClipsModal({
      clipIds: allSelectedClips.map(({ id }) => id),
      trackLocation: 'action-bar',
      initialTags: selectAll ? [] : commonTags,
    });
  }, [showEditTagsToClipsModal, allSelectedClips, selectAll]);

  const handleSelectAllAddToBoard = useCallback(() => {
    openSelectAllAddToBoardModal('action-bar');
  }, [openSelectAllAddToBoardModal]);

  const handleSelectAllMove = useCallback(() => {
    openSelectAllMoveItemsModal('action-bar');
  }, [openSelectAllMoveItemsModal]);

  const selectAllMenuOptions = useMemo<DropdownMenuOption[]>(
    () =>
      canRunSelectAllBoardMembershipAction
        ? [
            {
              ...getDropdownMenuOption('move'),
              onSelect: handleSelectAllMove,
              type: 'item',
            },
          ]
        : [],
    [canRunSelectAllBoardMembershipAction, handleSelectAllMove],
  );

  const insertSelectedClipsIntoCanvas = useCallback(() => {
    if (useEditCanvasStore.getState().readOnly) {
      return;
    }
    const allClips = [...selectedMediaClipItems, ...selectedFileItems];
    const allPayloads = allClips.map(clipToCanvasAssetPayload);

    const existingNodes = useEditCanvasStore.getState().nodes;

    const startX =
      existingNodes.length > 0
        ? existingNodes.reduce((max, node) => {
            const width = typeof node.style?.width === 'number' ? node.style.width : DEFAULT_CANVAS_IMAGE_WIDTH;

            return Math.max(max, node.position.x + width);
          }, 0) + GRID_GAP
        : 0;
    const startY = existingNodes[0]?.position.y ?? 0;

    const layout: ImageNodesLayout = {
      kind: 'row',
      startX,
      startY,
      spacing: CANVAS_IMAGE_NODE_SPACING,
    };

    const {
      nodes: nodesToAdd,
      addedCount,
      unsupportedCount,
      videoRejections,
    } = buildNodesFromPayloads(allPayloads, layout);

    if (nodesToAdd.length > 0) {
      execute(commands.addNodes(nodesToAdd, { zoomToFit: false, zoomToFitIfFirstNode: true }));

      const canvasId = useEditCanvasStore.getState().canvasId;
      const sourceBoardId = CentralizedBoardSelectors.boardId(store.getState());

      if (canvasId) {
        trackCanvasMediaImported({
          canvas_id: canvasId,
          number_of_media: nodesToAdd.length,
          source: 'workspace',
          ...(sourceBoardId && { board_id: sourceBoardId }),
        });
      }
    }

    const totalUnsupported = unsupportedCount + selectedBoardItems.length;
    const message = getAssetDropToastMessage(addedCount, totalUnsupported, 'canvas', videoRejections);

    if (message) {
      setInsertionToastMessage(message);
    }

    resetSelectAll();
    dispatch(resetSelectedItemsAction());
  }, [
    dispatch,
    execute,
    resetSelectAll,
    selectedMediaClipItems,
    selectedFileItems,
    selectedBoardItems,
    setInsertionToastMessage,
    store,
    trackCanvasMediaImported,
  ]);

  const handleInsert = useCallback(() => {
    if (!generativeCanvasEnabled || !canConsumeMoreCredits) {
      return;
    }
    insertSelectedClipsIntoCanvas();
  }, [canConsumeMoreCredits, generativeCanvasEnabled, insertSelectedClipsIntoCanvas]);

  const canCreateCanvas = canCreateCanvasInWorkspace(workspacePermissions);

  const handleAddToCanvas = useCallback(() => {
    if (!generativeCanvasEnabled) {
      return;
    }
    if (!canConsumeMoreCredits) {
      showCreditsDepletedModal({ triggeredBy: 'canvas' });
      return;
    }

    const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
    const selectedItems = selectedItemsSelector(store.getState());
    const selectedClipItems = selectedItems.filter((item) => item.type === 'asset' || item.type === 'file');
    const uniqueSelectedBoardIds = new Set(selectedClipItems.map((item) => item.selectedBoard?.id).filter(Boolean));
    const isMultipleBoards = uniqueSelectedBoardIds.size > 1;

    const allClips = [...selectedMediaClipItems, ...selectedFileItems];
    const allPayloads = allClips.map(clipToCanvasAssetPayload);

    const layout: ImageNodesLayout = {
      kind: 'row',
      startX: 0,
      startY: 0,
      spacing: CANVAS_IMAGE_NODE_SPACING,
    };

    const {
      nodes: initialNodes,
      addedCount,
      unsupportedCount,
      videoRejections,
    } = buildNodesFromPayloads(allPayloads, layout);

    const totalUnsupported = unsupportedCount + selectedBoardItems.length;
    const insertionToastMessage = getAssetDropToastMessage(addedCount, totalUnsupported, 'canvas', videoRejections);

    openDraftCanvas({
      analyticsContext: { source: 'asset_selection_bar' },
      boardId: isMultipleBoards ? undefined : (currentBoardId ?? undefined),
      initialNodes: initialNodes.length > 0 ? initialNodes : undefined,
      insertionToastMessage,
    });

    resetSelectAll();
    dispatch(resetSelectedItemsAction());
  }, [
    canConsumeMoreCredits,
    dispatch,
    generativeCanvasEnabled,
    openDraftCanvas,
    resetSelectAll,
    selectedBoardItems,
    selectedFileItems,
    selectedMediaClipItems,
    showCreditsDepletedModal,
    store,
  ]);

  const { menuOptions, actions } = usePrivateSelectionBarMenu({
    collectionsEnabled,
    canDownloadAllClips,
    canDownloadAllBoards,
    onBulkDownload: handleBulkDownload,
    onBoardFavorite: handleBoardFavorite,
    onBoardShare: handleBoardShare,
    onCanvasShare: handleCanvasShare,
    onBoardDownload: handleBoardDownload,
    onClipFavorite: handleClipFavorite,
    onClipShare: handleClipShare,
    onAssetDownload: handleAssetDownload,
    onSingleClipEditTags: handleSingleClipEditTags,
    onMultipleClipsEditTags: handleMultipleClipsEditTags,
    onCollectionShare: handleCollectionShare,
    onCompare: handleCompare,
    onAddToCanvas: canCreateCanvas ? handleAddToCanvas : undefined,
    onInsert: canvasReadOnly ? undefined : handleInsert,
    insertCount: selectedMediaClipItems.length + selectedFileItems.length + selectedBoardItems.length,
    layoutTierFlags,
  });

  // Select-all reaches beyond what is loaded, so per-item CTAs and the full menu stand down. Add to
  // board stays a dedicated button; move lives in the ellipsis; bulk tag/custom-field edits stay
  // on the suffix.
  const selectAllActions = useMemo(
    (): SelectionBarActions => ({
      addToBoard: canRunSelectAllBoardMembershipAction ? { onSelect: handleSelectAllAddToBoard } : undefined,
      editTags: { onSelect: handleMultipleClipsEditTags },
      customFields:
        selectAllCustomFieldMenuOptions.length > 0 ? { options: selectAllCustomFieldMenuOptions } : undefined,
    }),
    [
      canRunSelectAllBoardMembershipAction,
      handleMultipleClipsEditTags,
      handleSelectAllAddToBoard,
      selectAllCustomFieldMenuOptions,
    ],
  );

  const selectAllCompactTuck = useMemo(() => {
    if (!selectAll || !layoutTierFlags.hidePrefixActions) {
      return null;
    }

    const editTagsOption: DropdownMenuOption = {
      ...getDropdownMenuOption('edit-tags'),
      onSelect: handleMultipleClipsEditTags,
      type: 'item',
    };
    const addToBoardOption = canRunSelectAllBoardMembershipAction
      ? ({
          ...getDropdownMenuOption('add-to-board'),
          onSelect: handleSelectAllAddToBoard,
          type: 'item',
        } satisfies DropdownMenuOption)
      : null;
    const customFieldsSubmenu =
      selectAllCustomFieldMenuOptions.length > 0
        ? ({
            ...getDropdownMenuOption('bulk-edit-custom-fields'),
            options: selectAllCustomFieldMenuOptions,
            type: 'sub',
          } satisfies DropdownMenuOption)
        : null;

    return applySelectAllCompactTuck({
      menuOptions: selectAllMenuOptions,
      actions: selectAllActions,
      editTagsOption,
      addToBoardOption,
      customFieldsSubmenu,
    });
  }, [
    canRunSelectAllBoardMembershipAction,
    handleMultipleClipsEditTags,
    handleSelectAllAddToBoard,
    layoutTierFlags.hidePrefixActions,
    selectAll,
    selectAllActions,
    selectAllCustomFieldMenuOptions,
    selectAllMenuOptions,
  ]);

  const resolvedSelectAllOutput = selectAllCompactTuck
    ? { menuOptions: selectAllCompactTuck.options, actions: selectAllCompactTuck.actions }
    : { menuOptions: selectAllMenuOptions, actions: selectAllActions };

  return {
    menuOptions: selectAll ? resolvedSelectAllOutput.menuOptions : menuOptions,
    actions: selectAll ? resolvedSelectAllOutput.actions : actions,
    omitBoardName: isRightPanelOpen,
    stackLeftBarButtons: generativeCanvasEnabled && isRightPanelOpen,
    selectAllEnabled: selectAllEnabled && !hasOnlyCanvasesSelected,
    selectAllTotalCount,
  };
};
